import type { CursorData } from '@alpod-ai-writer/shared';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import { useCallback, useEffect, useState } from 'react';

export type AwarenessFieldSelection = CursorData & { clientId: string };

export function useYDocAwarenessSelection(provider: HocuspocusProvider) {
    const resolveSelection = () => {
        const selection = new Map<string, AwarenessFieldSelection[]>();

        provider.awareness?.getStates().forEach((state, clientId) => {
            if (clientId === provider.awareness?.clientID) {
                return;
            }

            const selectionField = state.selection_field as string | null;

            if (selectionField) {
                if (!selection.has(selectionField)) {
                    selection.set(selectionField, []);
                }

                selection.get(selectionField)?.push({
                    clientId,
                    ...state.data
                });
            }
        });

        return selection;
    };

    const [ selection, setSelection ] = useState<Map<string, AwarenessFieldSelection[]>>(resolveSelection());

    const setAwarenessToField = useCallback((documentField: string) => {
        provider.awareness?.setLocalStateField('selection_field', documentField);
    }, [ provider ]);

    const resetAwareness = useCallback(() => {
        provider.awareness?.setLocalStateField('selection_field', null);
    }, [ provider ]);

    useEffect(() => {
        const updateState = () => {
            setSelection(resolveSelection());
        };

        provider.awareness?.on('change', updateState);

        return () => {
            provider.awareness?.off('change', updateState);
        };
    }, [ provider ]);

    return {
        awarenessSelection: selection,
        setAwarenessToField,
        resetAwareness
    };
}

export function useYDocAwarenessFieldSelection(provider: HocuspocusProvider, documentField: string) {
    const resolveSelection = () => Array.from(provider.awareness?.getStates().entries() ?? [])
        .filter(([ key ]) => key !== provider.awareness?.clientID)
        .filter(([ , state ]) => state.selection_field === documentField)
        .map(([ key, state]) => ({
            clientId: key,
            ...state.data
        }));

    const [ selectedBy, setSelectedBy ] = useState<AwarenessFieldSelection[]>(resolveSelection());

    const setAwarenessToField = useCallback(() => {
        provider.awareness?.setLocalStateField('selection_field', documentField);
    }, [ provider ]);

    const resetAwareness = useCallback(() => {
        provider.awareness?.setLocalStateField('selection_field', null);
    }, [ provider ]);

    useEffect(() => {
        const updateState = () => {
            setSelectedBy(resolveSelection());
        };

        provider.awareness?.on('change', updateState);

        return () => {
            provider.awareness?.off('change', updateState);
        };
    }, [ provider ]);

    return {
        selectedBy,
        setAwarenessToField,
        resetAwareness
    };
}
